/**
 * Component for Customer Incentives settings
 * that allows merchants to provide incentives to their customers
 * either store credit or discount codes
 *
 * @component
 */

import {
  Alert,
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  message,
  Popconfirm,
  Row,
  Select,
  Table,
} from 'antd';
import axios from 'axios';
import React, { createRef } from 'react';
import { PageButtonHeader } from '../../../../../components/CustomHeader';
import {
  IconGift,
  IconTray,
} from '../../../../../components/CustomReturnsIcons';
import { SubscriptionContext } from '../../../../../contexts/SubscriptionContext';
import '../../../../../styles/App.css';
import Incentives from './Incentives';
const { Option } = Select;
const FormItem = Form.Item;
class CustomerIncentives extends React.Component {
  static contextType = SubscriptionContext;
  constructor(props) {
    super(props);
    this.state = {
      discountCodeResolution: 'storeCredit',
      discountCodeType: 'fixed',
      bonusCreditType: 'fixed',
      bonusCreditValue: 0.0,
      discountCodeValue: 0.0,
      bonusCreditResolution: 'exchangeItem',
      currentIncentive: {},
      data: [],
      resolutionOptions: {
        exchangeItem: 'Exchange',
        storeCredit: 'Store credit',
        storeWideExchange: 'Store-Wide Exchange',
        advancedExchange: 'Collection Exchange',
      },
      enableCustomerIncentives: false,
      loading: true,
    };

    this.editIncentiveRef = createRef();
  }

  componentDidMount = async () => {
    this.initializeComponent();
  };

  initializeComponent = async () => {
    try {
      await this.getIncentives();
      await this.getReturnSettings();
      this.setState({ loading: false });
    } catch (err) {
      this.setState({ loading: false });
      message.error('Error loading customer incentives. Please try again.', 4);
    }
  };

  getReturnSettings = async () => {
    try {
      const response = await axios.get('/api/returnSettings');
      if (response.data == null) {
        return;
      }
      this.setState({
        enableCustomerIncentives: response.data.enableCustomerIncentives,
      });
    } catch (err) {
      message.error('Error retrieving your return settings. Try again.', 5);
    }
  };

  getIncentives = async () => {
    try {
      const response = await axios.get('/api/returnSettings/getIncentives');
      if (response.data == null) {
        return;
      }
      this.setState({
        data: response.data,
      });
    } catch (err) {
      message.error('Error retrieving your return settings. Try again.', 5);
    }
  };

  getResoulutions = (skipState) => {
    let resolutionOptions = {
      exchangeItem: 'Exchange',
      storeCredit: 'Store Credit',
    };

    if (this.context.featureStoreWideExchange) {
      resolutionOptions = {
        ...resolutionOptions,
        storeWideExchange: 'Store-Wide Exchange',
      };
    }
    if (this.context.featureAdvancedExchanges) {
      resolutionOptions = {
        ...resolutionOptions,
        advancedExchange: 'Collection Exchange',
      };
    }
    if (!skipState) {
      this.setState({
        resolutionOptions,
      });
    }

    const options = Object.entries(resolutionOptions).map(([key, value]) => (
      <Option key={key} value={key}>
        {value}
      </Option>
    ));
    return options;
  };
  toggleEditMode = (e, record, toggle) => {
    if (!toggle) {
      this.getIncentives();
    }
    this.setState({ currentIncentive: record, editIncentive: toggle });
  };

  deleteIncentive = async (record) => {
    try {
      const response = await axios.post('/api/returnSettings/deleteIncentive', {
        id: record.id,
      });
      if (response.error) {
        return message.error('Error deleting the incentive. Try again.', 3);
      }
      this.getIncentives();
    } catch (err) {
      message.error('Error deleting the incentive. Try again.', 2);
    }
  };

  updateSettings = async () => {
    try {
      const response = await axios.post('/api/returnSettings/update', {
        enableCustomerIncentives: this.state.enableCustomerIncentives,
      });
      if (response.data.status === 'error') {
        return message.error(
          'Error updating your customer incentives. Please try again-----.',
          3
        );
      }
      if (!this.state.enableCustomerIncentives)
        return message.warning('Incentives Disabled', 3);
    } catch (err) {
      message.error('Error while updating customer incentives', 3);
    }
  };

  handleChange = (e) => {
    const target = e.target.name;
    this.setState(
      {
        [target]: e.target.checked,
      },
      () => this.updateSettings()
    );
  };

  render() {
    const columns = [
      {
        title: (
          <span data-intercom-target="col-header-incentive-type-configuration-customer-incentives">
            Incentive Type
          </span>
        ),
        dataIndex: 'incentiveType',
      },
      {
        title: (
          <span data-intercom-target="col-header-value-type-configuration-customer-incentives">
            Value Type
          </span>
        ),
        dataIndex: 'incentiveValueType',
        render: (text) => {
          return (
            <span>{text === 'fixed' ? 'Fixed Amount' : 'Percentage'}</span>
          );
        },
      },
      {
        title: (
          <span data-intercom-target="col-header-incentive-resolution-configuration-customer-incentives">
            Incentive Resolution
          </span>
        ),
        dataIndex: 'incentiveResolution',
        render: (text) => {
          return <span>{this.state.resolutionOptions[text]}</span>;
        },
      },
      {
        title: (
          <span data-intercom-target="col-header-incentive-value-configuration-customer-incentives">
            Incentive Value
          </span>
        ),
        dataIndex: 'incentiveValue',
        render: (text) => parseFloat(text)?.toFixed(2),
      },
      {
        title: (
          <span data-intercom-target="col-header-timer-configuration-customer-incentives">
            Timer
          </span>
        ),
        dataIndex: 'enableTimer',
        render: (value) => {
          return value === true ? <span>ON</span> : <span>OFF</span>;
        },
      },
      {
        title: (
          <span data-intercom-target="col-header-edit-configuration-customer-incentives">
            Edit
          </span>
        ),
        dataIndex: 'edit',
        key: (text, record) => {
          return `edit-${record.id}`;
        },
        render: (text, record) => {
          return (
            <Button
              size="small"
              onClick={(e) => this.toggleEditMode(e, record, true)}
              data-intercom-target="edit-button-configuration-customer-incentives"
            >
              Edit
            </Button>
          );
        },
      },
      {
        title: (
          <span data-intercom-target="col-header-delete-configuration-customer-incentives">
            Delete
          </span>
        ),
        dataIndex: 'delete',
        key: (text, record) => {
          return `delete-${record.id}`;
        },
        render: (text, record) => {
          return (
            <Popconfirm
              title="Are you sure you want to delete this rule?"
              onConfirm={() => this.deleteIncentive(record)}
              okText="Yes"
              cancelText="No"
              data-intercom-target="delete-popconfirm-configuration-customer-incentives"
            >
              <Button
                size="small"
                data-intercom-target="delete-button-configuration-customer-incentives"
              >
                Delete
              </Button>
            </Popconfirm>
          );
        },
      },
    ];

    if (this.state.loading) {
      return (
        <div style={{ position: 'absolute', top: '50%', left: '50%' }}>
          <div className="GlobalLoader GlobalLoader--md"></div>
        </div>
      );
    }

    return (
      <React.Fragment>
        <PageButtonHeader
          headingTitle="Incentives"
          headingRightContent={
            this.state.editIncentive && (
              <div className="flex-col-xxs-12">
                <FormItem>
                  <Button
                    ghost
                    onClick={() => this.toggleEditMode(_, {}, false)}
                    className="u-marginRight--sm"
                    data-intercom-target="cancel-button-configuration-customer-incentives"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    onClick={(() => {
                      if (this.editIncentiveRef) {
                        this.editIncentiveRef.current?.handleSubmit();
                      }
                    }).bind(this)}
                    data-intercom-target="save-changes-button-configuration-customer-incentives"
                  >
                    Save changes
                  </Button>
                </FormItem>
              </div>
            )
          }
        />
        {this.context.featureCustomerIncentives ? (
          <React.Fragment>
            <React.Fragment>
              {!this.state.editIncentive && (
                <React.Fragment>
                  {this.context.featureCustomerIncentives && (
                    <React.Fragment>
                      <Row type="flex">
                        <Col span={24}>
                          <div className="ContentPanel">
                            <div className="flex-row flex-middle-xxs u-columnGap--md u-rowGap--lg">
                              <div>
                                <IconGift size="md"></IconGift>
                              </div>
                              <div>
                                <h3 data-intercom-target="customer-incentives-title-configuration-customer-incentives">
                                  Customer incentives
                                </h3>
                                <p className="u-marginBottom--sm">
                                  Boost your revenue by encouraging customers to
                                  choose store credits and exchanges over
                                  refunds. Learn how to set up Customer
                                  Incentives{' '}
                                  <a
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href="https://support.richreturns.io/en/articles/17693642-customer-incentives?utm_source=merchant_panel"
                                  >
                                    here
                                  </a>
                                </p>
                                <Checkbox
                                  name="enableCustomerIncentives"
                                  checked={this.state.enableCustomerIncentives}
                                  onChange={(e) => this.handleChange(e)}
                                  data-intercom-target="enable-incentives-checkbox-configuration-customer-incentives"
                                >
                                  Enable incentives
                                </Checkbox>
                              </div>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </React.Fragment>
                  )}
                  {this.state.enableCustomerIncentives && (
                    <React.Fragment>
                      {/* <Row type="flex" justify="space-around" align="top">
                        <Col span={18}>
                          <Divider />
                        </Col>
                      </Row> */}
                      <div className="ContentPanel">
                        <div style={{ overflowX: 'auto' }}>
                          <div style={{ minWidth: 768 }}>
                            {this.state.resolutionOptions &&
                              Object.keys(this.state.resolutionOptions)
                                ?.length > 0 && (
                                <Table
                                  rowKey={(_, index) => index}
                                  columns={columns}
                                  dataSource={this.state.data}
                                  locale={{
                                    emptyText: (
                                      <div className="flex-row flex-middle-xxs flex-center-xxs">
                                        <div className="flex-col-xxs-12">
                                          <IconTray size="md" />
                                        </div>
                                        <div>
                                          <p className="TextBody TextBody--xxxs">
                                            No Data
                                          </p>
                                        </div>
                                      </div>
                                    ),
                                  }}
                                />
                              )}
                          </div>
                        </div>
                      </div>
                      <Row
                        type="flex"
                        justify="end"
                        className="u-marginTop--lg"
                      >
                        <Col>
                          <Button
                            type="primary"
                            onClick={() =>
                              this.setState({ editIncentive: true })
                            }
                            data-intercom-target="add-incentive-button-configuration-customer-incentives"
                          >
                            {' '}
                            Add Incentive
                          </Button>
                        </Col>
                      </Row>
                    </React.Fragment>
                  )}
                </React.Fragment>
              )}
              {this.state.editIncentive && (
                <Incentives
                  ref={this.editIncentiveRef}
                  currentIncentive={this.state.currentIncentive}
                  getResoulutions={this.getResoulutions}
                  toggleEditMode={this.toggleEditMode}
                />
              )}
            </React.Fragment>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <p>
              Boost revenue by encouraging customers to choose store credits and
              exchanges instead of refunds
            </p>
            <Row type="flex" justify="space-around" align="top">
              <Col span={24}>
                <Alert
                  message="Already on our Premium or Enterprise plan?"
                  description={
                    <React.Fragment>
                      <span>
                        Please reach out to us directly in order to have this
                        feature enabled and set up for your account.
                      </span>
                    </React.Fragment>
                  }
                  type="info"
                  showIcon
                  data-intercom-target="premium-plan-alert-configuration-customer-incentives"
                />
              </Col>
            </Row>
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }
}

export default CustomerIncentives;
